import moment from "moment";
import { downloadXlsx } from "./xlsx";
import { STATUS_LABEL, VISIT_TYPE_LABEL, DELAY_STATUS_LABEL } from "./appointment-filters";

// Column layout is fixed and ordered — it's the contract the exported file
// makes with whoever opens it downstream.
export const EXPORT_COLUMNS = [
  { header: "Id", width: 12 },
  { header: "Provider", width: 22 },
  { header: "Provider Type", width: 14 },
  { header: "Provider Mobile", width: 16 },
  { header: "Type", width: 14 },
  { header: "Status", width: 14 },
  { header: "Patient Name", width: 22 },
  { header: "Mobile", width: 14 },
  { header: "Gender", width: 10 },
  { header: "Date of Birth", width: 14 },
  { header: "City", width: 16 },
  { header: "Pincode", width: 10 },
  { header: "Appointment Date", width: 20 },
  { header: "Duration (min)", width: 14 },
  { header: "Speciality", width: 20 },
  { header: "Sub Speciality", width: 16 },
  { header: "Procedure", width: 18 },
  { header: "Cancel Reason", width: 28 },
  { header: "Cancelled By", width: 20 },
  { header: "Notes", width: 28 },
  { header: "Created At", width: 20 },
];

// Slots in the booking template are 20 minutes apart.
const SLOT_DURATION_MINUTES = 20;

// Blank cells read as missing data; a dash reads as "checked, nothing there".
const DASH = "-";
const orDash = (value) => (value === 0 ? 0 : value || DASH);

const fmtDate = (value, format = "YYYY-MM-DD") =>
  value && moment(value).isValid() ? moment(value).format(format) : DASH;

/** One appointment -> one row, in EXPORT_COLUMNS order. */
export function toExportRow(a) {
  const patient = a.patient_id || {};
  const doctor = a.doctor_id || {};
  const address = patient.address || {};

  const appointmentAt = a.iso_date
    ? `${moment(a.iso_date, "YYYY-MM-DD").format("YYYY-MM-DD")} ${a.slot_time || ""}`.trim()
    : DASH;

  // Only a cancellation has a "cancel reason"; other dispositions are the
  // outcome of a completed visit and belong in Notes.
  const isCancelled = a.status === "cancelled";

  const notes = [
    !isCancelled && a.disposition ? a.disposition : null,
    a.delay_status ? DELAY_STATUS_LABEL[a.delay_status] : null,
    a.last_edited_by_name ? `Edited by ${a.last_edited_by_name}` : null,
    a.on_leave ? "Provider on leave" : null,
  ]
    .filter(Boolean)
    .join("; ");

  return [
    a._id ? String(a._id).slice(-6) : DASH,
    orDash(doctor.name),
    doctor.name ? "Doctor" : DASH,
    orDash(doctor.phone),
    orDash(VISIT_TYPE_LABEL[a.visit_type] || a.visit_type),
    orDash(STATUS_LABEL[a.status] || a.status),
    orDash(patient.name),
    orDash(patient.phone),
    orDash(patient.gender),
    patient.dob ? fmtDate(patient.dob) : DASH,
    orDash(address.city),
    orDash(address.pincode),
    appointmentAt,
    SLOT_DURATION_MINUTES,
    orDash(doctor.specialty),
    DASH, // Sub Speciality — not modelled
    orDash(a.procedure),
    isCancelled ? orDash([a.disposition, a.cancellation_note].filter(Boolean).join(" — ")) : DASH,
    isCancelled ? orDash(a.cancelled_by_name) : DASH,
    notes || DASH,
    a.createdAt ? fmtDate(a.createdAt, "YYYY-MM-DD HH:mm") : DASH,
  ];
}

/** Builds and downloads the workbook. Returns how many rows were written. */
export function exportAppointments(appointments, filename) {
  const rows = appointments.map(toExportRow);
  downloadXlsx(
    filename || `appointments_export_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`,
    EXPORT_COLUMNS.map((c) => c.header),
    rows,
    { sheetName: "Appointments", widths: EXPORT_COLUMNS.map((c) => c.width) }
  );
  return rows.length;
}
